import React from "react"
import { Redirect } from "react-router"
import { Link } from "react-router-dom"
import { Query, Mutation } from "react-apollo"
import { Waypoint } from "react-waypoint"
import Dropdown from "../../components/Dropdown"
import SearchBar from "../../components/SearchBar"
import Loading from "../../components/Loading"
import VennTranscript from "../../components/VennTranscript"
import Button from "../../components/Button"
import gql from "graphql-tag"
import qs from "qs"
import moment from "moment"
import { etrColorInsights } from "../../shared/images"
import SuggestTopicModal from "./components/SuggestTopicModal"

import "./etrInsightsSearch.scss"

const fileIcons = {
  Transcript: "glyphicon glyphicon-duplicate",
  Abstract: "glyphicon glyphicon-file",
  "Event Deck": "glyphicon glyphicon-blackboard",
  "Full Recording": "glyphicon glyphicon-play-circle",
}

class EtrInsightsSearch extends React.Component {
  constructor(props) {
    super(props)

    let params = qs.parse(props.location.search, { ignoreQueryPrefix: true })

    this.state = {
      vendorKeyword: "",
      searchKeywordInput: params.keyword || "",
      vendorKeywordInput: "",
      openFirstResult: (params.keyword || "").trim() !== "",
      hasMoreTranscripts: true,
      isOpen: {},
      isSuggestTopicModalOpen: false,
      didSubmitTopic: false,
      topicSubmitError: null,
    }

    this.vendorKeywordTimeout
    this.searchKeywordTimeout
  }

  toggleTranscript = (productId) => {
    this.setState((state) => ({
      isOpen: {
        ...state.isOpen,
        [productId]: !state.isOpen[productId],
      },
    }))
  }

  updateVendor = (selected) => {
    this.setState({ isOpen: {} }, () => {
      let params = qs.parse(this.props.location.search, {
        ignoreQueryPrefix: true,
      })
      if (selected && selected.value) {
        params.vendor = selected.value
        this.props.trackEvent("Updated transcript search", { ...params })
      } else {
        delete params.vendor
      }

      this.props.history.replace({
        ...this.props.location,
        search: qs.stringify(params),
      })
    })
  }

  updateVendorInput = (text) => {
    clearTimeout(this.vendorKeywordTimeout)
    this.setState({ vendorKeywordInput: text }, () => {
      this.vendorKeywordTimeout = setTimeout(
        () => this.updateVendorKeyword(),
        400
      )
    })
  }

  updateVendorKeyword = () => {
    this.setState((state) => ({
      vendorKeyword: state.vendorKeywordInput,
    }))
  }

  updateSearchInput = (e) => {
    clearTimeout(this.searchKeywordTimeout)
    this.setState({ searchKeywordInput: e.target.value, isOpen: {} }, () => {
      this.searchKeywordTimeout = setTimeout(
        () => this.updateSearchKeyword(),
        400
      )
    })
  }

  updateSearchKeyword = () => {
    clearTimeout(this.searchKeywordTimeout)
    this.setState(
      (state) => ({
        openFirstResult: state.searchKeywordInput.trim() !== "",
        isOpen: {},
      }),
      () => {
        let params = qs.parse(this.props.location.search, {
          ignoreQueryPrefix: true,
        })
        if (this.state.searchKeywordInput) {
          params.keyword = this.state.searchKeywordInput
          this.props.trackEvent("Updated transcript search", { ...params })
        } else {
          delete params.keyword
        }

        this.props.history.replace({
          ...this.props.location,
          search: qs.stringify(params),
        })
      }
    )
  }

  resetSearch = () => {
    clearTimeout(this.vendorKeywordTimeout)
    clearTimeout(this.searchKeywordTimeout)
    this.setState(
      {
        searchKeywordInput: "",
        vendorKeyword: "",
        vendorKeywordInput: "",
        openFirstResult: false,
        isOpen: {},
      },
      () => {
        this.props.history.replace({
          ...this.props.location,
          search: null,
        })
      }
    )
  }

  trackView = (product, file) => {
    this.props.trackEvent("Venn Clicked View report", {
      productName: product.name,
      productId: product.id,
      productType: product.proprietaryDataset,
      file: file.name,
    })
  }

  _renderSEO = () => {
    return null
  }

  _renderVendorDropdown = () => {
    const params = qs.parse(this.props.location.search, {
      ignoreQueryPrefix: true,
    })
    const query = gql`
      query topicSearch($keyword: String, $offset: Int) {
        topics(type: "Vendor", keyword: $keyword, offset: $offset) {
          id
          value: name
          name
        }
      }
    `

    const queryConfig = {
      query,
      variables: {
        keyword: this.state.vendorKeyword,
      },
    }

    return (
      <Query {...queryConfig}>
        {({ error, data, fetchMore, networkStatus }) => {
          if (error || !data.topics) return null

          return (
            <Dropdown
              selectAll
              label="COMPANY: "
              options={data.topics}
              inputValue={this.state.vendorKeywordInput}
              onChange={this.updateVendor}
              onInputChange={this.updateVendorInput}
              value={
                params.vendor && { value: params.vendor, label: params.vendor }
              }
              filterOption={() => true}
              onMenuScrollToBottom={() => {
                if (networkStatus === 7) {
                  fetchMore({
                    variables: {
                      offset: data.topics.length,
                    },
                    updateQuery: (prev, { fetchMoreResult }) => {
                      if (!fetchMoreResult) return prev
                      return Object.assign({}, prev, {
                        topics: [
                          ...(prev.topics || []),
                          ...(fetchMoreResult.topics || []),
                        ],
                      })
                    },
                  })
                }
              }}
            />
          )
        }}
      </Query>
    )
  }

  _renderResultsList = () => {
    const params = qs.parse(this.props.location.search, {
      ignoreQueryPrefix: true,
    })
    const query = gql`
      query searchVennTranscripts(
        $vendor: String
        $keyword: String
        $offset: Int
      ) {
        results: searchVennTranscripts(
          vendor: $vendor
          keyword: $keyword
          offset: $offset
        ) {
          id
          name
          description
          slug
          proprietaryDataset
          metadata
          files {
            name
            url
            filetype
            downloadUrl
          }
          snippetCount(keyword: $keyword)
        }
      }
    `

    const queryConfig = {
      query,
      ssr: false, // Needed to set this to false since onCompleted isn't called on server-side render
      fetchPolicy: "cache-and-network",
      variables: {
        vendor: params.vendor || null,
        keyword: params.keyword || "",
      },
      onCompleted: (data) => {
        this.setState((state) => {
          if (state.openFirstResult) {
            let isOpen = { ...state.isOpen }
            if (data.results[0]) {
              isOpen[data.results[0].id] = true
            }

            return {
              openFirstResult: false,
              isOpen,
            }
          }
          return null
        })
      },
    }

    return (
      <Query {...queryConfig}>
        {({ loading, error, data, fetchMore, networkStatus, variables }) => {
          if (data && data.results) {
            if (data.results.length === 0) {
              return (
                <div className="no-results">
                  <h2>We're Sorry!</h2>
                  <p>
                    We can't seem to find any ETR Insights transcripts that
                    match your search. Try modifying your search keyword or
                    selecting a different vendor.
                  </p>
                  <Button
                    classes={["dark-fill", "small-caps"]}
                    action={this.resetSearch}
                  >
                    Reset Search
                  </Button>
                </div>
              )
            }
            return (
              <React.Fragment>
                {data.results.map((product) => {
                  return (
                    <div key={product.id} className="vennsearch-result">
                      <div className="vennsearch-result-header">
                        <div className="foobar">
                          <div className="vennsearch-result-logo">
                            <img
                              src={etrColorInsights}
                              className="logo-md"
                              alt="ETR Insights"
                            />
                          </div>
                          <div className="title-section">
                            <h3>{product.name}</h3>
                            <p className="subtitle">
                              {moment
                                .utc(product.metadata["Event Date"])
                                .format("LL")}
                            </p>
                          </div>
                          <h3 className="margin-top">OVERVIEW</h3>
                          <p>{product.description}</p>
                          <div style={{ clear: "both" }} />
                        </div>
                        <hr />
                        <div className="vennsearch-result-files">
                          <h3>Click to view/download: </h3>
                          {(product.files || []).map((file, i) => (
                            <Link
                              key={i}
                              className="file-download"
                              to={{
                                pathname:
                                  "/marketplace/" + product.slug[0] + "/view",
                                search: qs.stringify(
                                  { item: i },
                                  { addQueryPrefix: true }
                                ),
                              }}
                              onClick={() => this.trackView(product, file)}
                            >
                              <span
                                className={`file-icon ${fileIcons[file.name]}`}
                              />
                              <h4>{file.name.toUpperCase()}</h4>
                            </Link>
                          ))}
                        </div>
                      </div>
                      <div
                        className={`vennsearch-result-content ${
                          this.state.isOpen[product.id] ? "open" : ""
                        }`}
                      >
                        <div
                          className="vennsearch-result-content-header"
                          onClick={() => this.toggleTranscript(product.id)}
                        >
                          <h3>
                            VIEW{" "}
                            {product.snippetCount <= 0
                              ? "TRANSCRIPT"
                              : `${product.snippetCount} SNIPPET RESULT${
                                  product.snippetCount === 1 ? "" : "S"
                                }`}
                          </h3>
                          <span
                            className={`collapse-icon glyphicon glyphicon-menu-${
                              this.state.isOpen[product.id] ? "up" : "down"
                            }`}
                          />
                        </div>
                        {this.state.isOpen[product.id] && (
                          <VennTranscript
                            productId={product.id}
                            initialKeyword={variables.keyword}
                            trackEvent={this.props.trackEvent}
                          />
                        )}
                      </div>
                    </div>
                  )
                })}
                {this.state.hasMoreTranscripts && !loading && (
                  <div id="loading-more-transcripts">
                    <Waypoint
                      onEnter={() => {
                        if (networkStatus === 7) {
                          fetchMore({
                            variables: {
                              offset: data.results.length,
                            },
                            updateQuery: (prev, { fetchMoreResult }) => {
                              if (!fetchMoreResult) return prev
                              return Object.assign({}, prev, {
                                results: [
                                  ...(prev.results || []),
                                  ...(fetchMoreResult.results || []),
                                ],
                              })
                            },
                          }).then(({ data, loading, error }) => {
                            if (
                              !error &&
                              !loading &&
                              data.results.length === 0
                            ) {
                              this.setState({ hasMoreTranscripts: false })
                            }
                          })
                        }
                      }}
                    />
                    <Loading />
                  </div>
                )}
              </React.Fragment>
            )
          } else if (loading) {
            return <Loading />
          } else if (error) {
            return (
              <div className="no-results">
                <h2>Oops!</h2>
                <p>
                  We had some trouble fetching the ETR Insights transcripts
                  matching your search.
                </p>
              </div>
            )
          }
          return null
        }}
      </Query>
    )
  }

  render = () => {
    const query = gql`
      query canSearchVennTranscripts {
        viewer {
          id
          canSearchVennTranscripts
          isUserAuthenticated
        }
      }
    `

    return (
      <Query query={query} fetchPolicy="network-only">
        {({ loading, error, data, client }) => {
          if (error) {
            throw new Error(error)
          }

          if (loading && !data.viewer) {
            return <Loading />
          }

          if (data.viewer && !data.viewer.isUserAuthenticated) {
            return (
              <Redirect
                to={{
                  pathname: "/login",
                  state: { from: "/etr-insights/search" },
                }}
              />
            )
          }

          if (!loading && !data.viewer.canSearchVennTranscripts) {
            return (
              <Redirect
                to={{
                  pathname: "/etr-insights/search/about",
                }}
              />
            )
          }

          return (
            <div
              className="vennsearch-container"
              ref={(ref) => (this.container = ref)}
            >
              <Mutation
                mutation={gql`
                  mutation createInsightsTopicSuggestion($text: String!) {
                    createInsightsTopicSuggestion(text: $text)
                  }
                `}
              >
                {(createInsightsTopicSuggestion, { loading }) => {
                  return (
                    <SuggestTopicModal
                      error={this.state.topicSubmitError}
                      isOpen={this.state.isSuggestTopicModalOpen}
                      didSubmit={this.state.didSubmitTopic}
                      submitDisabled={loading}
                      onReset={() => {
                        this.setState({
                          topicSubmitError: null,
                          didSubmitTopic: false,
                        })
                      }}
                      onClose={() => {
                        this.setState({
                          isSuggestTopicModalOpen: false,
                          didSubmitTopic: false,
                        })
                        this.props.trackEvent("Insights: Topic Suggestion", {
                          action: "Close topic suggestion modal button click",
                        })
                      }}
                      onSubmit={async (topicText) => {
                        try {
                          const { data } = await createInsightsTopicSuggestion({
                            variables: {
                              text: topicText,
                            },
                          })

                          if (
                            data &&
                            data.createInsightsTopicSuggestion === "success"
                          ) {
                            this.setState({
                              didSubmitTopic: true,
                              topicSubmitError: null,
                            })
                            this.props.trackEvent(
                              "Insights: Topic Suggestion",
                              {
                                action:
                                  "Submit a topic suggestion button click",
                                success: true,
                                topic: topicText,
                              }
                            )
                            return
                          }

                          const submitError = data
                            ? data.createInsightsTopicSuggestion
                            : "unknown"

                          this.setState({
                            topicSubmitError: submitError,
                          })
                          this.props.trackEvent("Insights: Topic Suggestion", {
                            action: "Submit a topic suggestion button click",
                            success: false,
                            error: submitError,
                            topic: topicText,
                          })
                        } catch {
                          const error = "unknown"
                          this.setState({ topicSubmitError: error })
                          this.props.trackEvent("Insights: Topic Suggestion", {
                            action: "Submit a topic suggestion button click",
                            success: false,
                            error: error,
                            topic: topicText,
                          })
                        }
                      }}
                    />
                  )
                }}
              </Mutation>
              {this._renderSEO()}
              <div className="filter-container">
                <div className="filter-body">
                  <h3 className="mobile-filter-header">
                    Search ETR Insights Transcripts:
                  </h3>
                  <div className="insights-filter-item">
                    <SearchBar
                      label="KEYWORD: "
                      classes={["border", "rounded", "insights-filter-item"]}
                      value={this.state.searchKeywordInput}
                      onChange={this.updateSearchInput}
                      placeholder="Search by company, product, keyword or quote"
                      onMagGlassClick={this.updateSearchKeyword}
                      onKeyUp={(e) => {
                        if (e.which === 13 || e.which === 27) {
                          this.updateSearchKeyword()
                        }
                      }}
                    />
                  </div>
                  <div className="insights-filter-item">
                    {this._renderVendorDropdown()}
                  </div>
                  <div className="insights-filter-item suggest-topic-btn">
                    <Button
                      classes={["insights-filter-item", "btn-primary"]}
                      text="Suggest Topic"
                      action={() => {
                        this.setState({ isSuggestTopicModalOpen: true })
                        this.props.trackEvent("Insights: Topic Suggestion", {
                          action: "Open topic suggestion modal button click",
                        })
                      }}
                    />
                  </div>
                </div>
              </div>
              {this._renderResultsList()}
            </div>
          )
        }}
      </Query>
    )
  }
}

export default EtrInsightsSearch
